import React, { useEffect, useRef, useState } from 'react';
import { Modal, View, Text, Pressable, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AdminInput from '../AdminInput';
import AdminSwitch from '../AdminSwitch';
import { TooltipCustom } from '../Tooltip';
import { ADMIN_COLORS, ADMIN_FONT } from '../../config/theme/adminColors';
import { SegmentedControl, useModalWidth } from '../../pages/AdminPanel/Products/formStyles';

// Configuração da categoria num só lugar.
//
// Antes cada opção era um ícone solto no cabeçalho da categoria (editar, copiar,
// excluir) e o "editar" trocava o cabeçalho por um formulário inline com ordem,
// título e os rádios de orientação. Além de poluído, `hideTitle` só existia na
// criação e não tinha como ser alterado depois. Aqui tudo vive num modal no
// padrão do painel admin, e a lista fica com um único ícone de engrenagem.

const ORIENTATION_OPTIONS = [
  {
    value: 'horizontal',
    label: 'Horizontal',
    description: 'Capas deitadas (16:9), como na Netflix.',
    icon: 'tablet-landscape-outline',
  },
  {
    value: 'vertical',
    label: 'Vertical',
    description: 'Capas em pé, formato pôster.',
    icon: 'tablet-portrait-outline',
  },
];

const VERTICAL_TOOLTIP =
  'As capas verticais aparecerão apenas no App Web até a atualização dos apps iOS e Android, de acordo com nosso cronograma.';

const CategorySettingsModal = ({
  visible,
  category,
  isSaving,
  onClose,
  onSave,
  onCopy,
  onDelete,
}) => {
  const modalWidth = useModalWidth();

  const [title, setTitle] = useState('');
  const [order, setOrder] = useState('');
  const [orientation, setOrientation] = useState('horizontal');
  const [hideTitle, setHideTitle] = useState(false);
  const [error, setError] = useState('');

  // Semeia o formulário a cada ABERTURA: assim ele reflete o que está salvo,
  // inclusive depois de uma edição cancelada.
  //
  // A semeadura é guardada pelo id da categoria, não pela referência do objeto:
  // a Main recalcula `filteredCategories` a cada render, então o mesmo item
  // chega aqui como um objeto novo sempre que ela renderiza. No celular o
  // teclado faz exatamente isso — abrir/fechar o teclado muda a altura da
  // janela (`useWindowDimensions` na Main), e o "certinho" do iPhone ou o
  // "Próximo" do Android disparavam um re-seed que descartava o texto recém
  // digitado.
  const seededIdRef = useRef(undefined);

  useEffect(() => {
    if (!visible) {
      seededIdRef.current = undefined;
      return;
    }

    const categoryId = category?.id ?? null;
    if (seededIdRef.current !== undefined && seededIdRef.current === categoryId) return;
    seededIdRef.current = categoryId;

    setTitle(category?.title ?? '');
    setOrder(category?.order == null ? '' : String(category.order));
    setOrientation(category?.orientation || 'horizontal');
    setHideTitle(!!category?.hideTitle);
    setError('');
  }, [visible, category]);

  const handleSave = () => {
    const trimmedTitle = (title || '').trim();
    if (!trimmedTitle) {
      setError('O título da categoria é obrigatório.');
      return;
    }

    const parsedOrder = Number(order);
    if (order !== '' && !Number.isFinite(parsedOrder)) {
      setError('Informe um número válido para a ordem.');
      return;
    }

    onSave({
      title: trimmedTitle,
      // Ordem vazia mantém a atual — não vale gravar NaN por um campo limpo.
      order: order === '' ? category?.order : parsedOrder,
      orientation,
      hideTitle,
    });
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={[styles.card, { width: modalWidth, maxWidth: 560 }]}>
          <View style={styles.header}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.title}>Configurações da categoria</Text>
              <Text style={styles.subtitle}>
                Nome, posição na tela inicial e formato das capas.
              </Text>
            </View>
            <Pressable
              onPress={onClose}
              hitSlop={8}
              style={({ hovered }) => [styles.closeBtn, hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle }]}
            >
              <Ionicons name="close" size={20} color={ADMIN_COLORS.muted} />
            </Pressable>
          </View>

          <ScrollView
            style={{ maxHeight: '60vh' }}
            contentContainerStyle={{ padding: 20 }}
            showsVerticalScrollIndicator
          >
            <Text style={styles.label}>Título</Text>
            <AdminInput
              placeholder="Digite o título da categoria"
              value={title}
              onChangeText={(value) => {
                setTitle(value);
                if (error) setError('');
              }}
            />

            <Text style={[styles.label, { marginTop: 18 }]}>Ordem</Text>
            <Text style={styles.hint}>Posição em que a categoria aparece na tela inicial.</Text>
            <AdminInput
              keyboardType="numeric"
              placeholder="Digitar ordem"
              value={order}
              onChangeText={(value) => {
                setOrder(value);
                if (error) setError('');
              }}
            />

            <View style={styles.switchRow}>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={styles.label}>Exibir título para os alunos</Text>
                <Text style={styles.hint}>
                  Desative para um visual mais limpo — o título continua valendo internamente.
                </Text>
              </View>
              <AdminSwitch
                value={!hideTitle}
                onValueChange={(value) => setHideTitle(!value)}
              />
            </View>

            {hideTitle ? (
              <View style={styles.warnBox}>
                <Text style={styles.warnText}>
                  O título ficará invisível para os alunos, mas continua sendo usado na busca,
                  nos filtros e na configuração de acesso. Sem um título claro, fica difícil
                  identificar a categoria nessas telas.
                </Text>
              </View>
            ) : null}

            <View style={[styles.labelRow, { marginTop: 20 }]}>
              <Text style={styles.label}>Formato das capas</Text>
              <TooltipCustom text={VERTICAL_TOOLTIP} />
            </View>
            <SegmentedControl
              options={ORIENTATION_OPTIONS}
              value={orientation}
              onChange={setOrientation}
            />

            {error ? <Text style={styles.error}>{error}</Text> : null}

            <View style={styles.divider} />

            <Text style={styles.sectionTitle}>Ações</Text>
            <View style={styles.actionsBlock}>
              <Pressable
                onPress={onCopy}
                disabled={isSaving}
                style={({ hovered }) => [
                  styles.actionBtn,
                  hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
                ]}
              >
                <Ionicons name="copy-outline" size={18} color={ADMIN_COLORS.ink} />
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.actionLabel}>Duplicar categoria</Text>
                  <Text style={styles.hint}>Cria uma cópia com todos os módulos e conteúdos.</Text>
                </View>
              </Pressable>

              <Pressable
                onPress={onDelete}
                disabled={isSaving}
                style={({ hovered }) => [
                  styles.actionBtn,
                  { borderColor: ADMIN_COLORS.dangerBorder },
                  hovered && { backgroundColor: ADMIN_COLORS.dangerSoft },
                ]}
              >
                <Ionicons name="trash-outline" size={18} color={ADMIN_COLORS.danger} />
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={[styles.actionLabel, { color: ADMIN_COLORS.danger }]}>
                    Excluir categoria
                  </Text>
                  <Text style={styles.hint}>
                    Remove a categoria e todo o conteúdo dela. Não dá para desfazer.
                  </Text>
                </View>
              </Pressable>
            </View>
          </ScrollView>

          <View style={styles.footer}>
            <Pressable
              onPress={onClose}
              disabled={isSaving}
              style={({ hovered }) => [
                styles.btn,
                styles.btnSecondary,
                hovered && { backgroundColor: '#E5E7EB' },
              ]}
            >
              <Text style={[styles.btnText, { color: ADMIN_COLORS.secondaryText }]}>Cancelar</Text>
            </Pressable>
            <Pressable
              onPress={handleSave}
              disabled={isSaving}
              style={({ hovered }) => [
                styles.btn,
                styles.btnPrimary,
                hovered && { backgroundColor: ADMIN_COLORS.accentDark },
                isSaving && { opacity: 0.7 },
              ]}
            >
              <Text style={[styles.btnText, { color: '#FFFFFF' }]}>
                {isSaving ? 'Salvando...' : 'Salvar'}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = {
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(17,24,39,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    backgroundColor: ADMIN_COLORS.surface,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    boxShadow: '0 12px 32px rgba(16,24,40,0.18)',
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.border,
  },
  title: { fontFamily: ADMIN_FONT, fontSize: 18, fontWeight: '700', color: ADMIN_COLORS.ink },
  subtitle: { fontFamily: ADMIN_FONT, fontSize: 13, color: ADMIN_COLORS.muted, marginTop: 4 },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  labelRow: { flexDirection: 'row', alignItems: 'center', gap: 2 },
  label: { fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: '600', color: ADMIN_COLORS.label },
  hint: { fontFamily: ADMIN_FONT, fontSize: 12, color: ADMIN_COLORS.muted, marginTop: 2, lineHeight: 16 },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 20,
  },
  warnBox: {
    marginTop: 12,
    backgroundColor: ADMIN_COLORS.warnBg,
    borderColor: ADMIN_COLORS.warnBorder,
    borderWidth: 1,
    borderRadius: 10,
    padding: 12,
  },
  warnText: { fontFamily: ADMIN_FONT, fontSize: 13, color: ADMIN_COLORS.warnText, lineHeight: 18 },
  error: { fontFamily: ADMIN_FONT, fontSize: 12, color: ADMIN_COLORS.danger, marginTop: 12, fontWeight: '500' },
  divider: { height: 1, backgroundColor: ADMIN_COLORS.border, marginVertical: 20 },
  sectionTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    fontWeight: '700',
    color: ADMIN_COLORS.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 10,
  },
  actionsBlock: { gap: 10 },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 10,
    padding: 12,
    cursor: 'pointer',
  },
  actionLabel: { fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: '600', color: ADMIN_COLORS.ink },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    padding: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.border,
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
  },
  btn: {
    minHeight: 44,
    borderRadius: 10,
    paddingHorizontal: 22,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  btnPrimary: { backgroundColor: ADMIN_COLORS.accent },
  btnSecondary: { backgroundColor: ADMIN_COLORS.secondaryBg },
  btnText: { fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: '600' },
};

export default CategorySettingsModal;
